import React, { useEffect, useState } from "react";
import API_URL from "../apiConfig";
import "../styles/DailyJournal.css";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/*
  DailyJournal.jsx
  - Fetches opening balances, income, expense and (optionally) transfer rows.
  - No ESLint no-mixed-operators warnings (ALL ambiguous expressions now clarified).
  - Distinct columns for Receipts and Payments.
  - Exports to Excel and PDF.
*/

// Utility: robust number conversion with Indian format
function toNumber(v) {
  if (v === undefined || v === null || v === "") return 0;
  const s = String(v).replace(/,/g, "");
  const n = Number(s);
  if (!Number.isFinite(n)) return 0;
  return Number(n.toFixed(2));
}
function to2(v) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// --- ESLint fix: ALL mixed operators now have explicit parentheses
function parseToYMD(dateStr) {
  if (!dateStr) return "";
  const s = String(dateStr).trim();

  // yyyy-mm-dd
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // dd-mm-yyyy
  if (/^\d{2}-\d{2}-\d{4}$/.test(s)) {
    const [d, m, y] = s.split("-");
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  // dd/mm/yyyy
  if (s.includes("/")) {
    const parts = s.split("/");
    if (parts.length === 3) {
      const [d, m, y] = parts;
      // FIXED ESLINT: wrap each && group in parentheses here:
      if (((/^\d{2}$/.test(d)) && (/^\d{2}$/.test(m)) && (/^\d{4}$/.test(y)))) {
        return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
      }
    }
  }
  const d = new Date(s);
  if (!isNaN(d)) return d.toISOString().slice(0, 10);
  return "";
}

function safeTs(dateStr) {
  const ymd = parseToYMD(dateStr);
  if (!ymd) return 0;
  const t = Date.parse(ymd);
  return Number.isNaN(t) ? 0 : t;
}

function formatDisplay(dateStr) {
  const ymd = parseToYMD(dateStr);
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return `${d}-${m}-${y}`;
}

function normalizeId(v) {
  if (v === undefined || v === null) return "";
  try {
    return String(v).trim().replace(/\s+/g, "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  } catch {
    return "";
  }
}

function finYearBounds(finYear) {
  if (!finYear || !finYear.includes("-")) return { start: "", end: "" };
  const [a, b] = finYear.split("-");
  return { start: `${a}-04-01`, end: `${b}-03-31` };
}

function inRangeYmd(ymd, start, end) {
  // No mixed &&/|| here - all explicit.
  if (!start && !end) return true;
  if (start) {
    if (!ymd) return false;
    if (ymd < start) return false;
  }
  if (end) {
    if (!ymd) return false;
    if (ymd > end) return false;
  }
  return true;
}

function filterOutTransferDuplicatesRaw(income = [], expense = [], transfers = []) {
  const idSet = new Set();
  const sigSet = new Set();
  (transfers || []).forEach(t => {
    const ids = [
      normalizeId(t.voucher_no),
      normalizeId(t.voucher),
      normalizeId(t.receipt_no),
      normalizeId(t.reference),
      normalizeId(t.id)
    ].filter(Boolean);
    ids.forEach(i => idSet.add(i));
    const date = parseToYMD(t.date) || "";
    const amt = toNumber(t.amount || 0);
    sigSet.add(`${date}|${amt}`);
  });
  const isDup = (r) => {
    const ids = [
      normalizeId(r.voucher),
      normalizeId(r.voucher_no),
      normalizeId(r.receipt_no),
      normalizeId(r.reference),
      normalizeId(r.id)
    ].filter(Boolean);
    if (ids.some(i => idSet.has(i))) return true;
    const date = parseToYMD(r.date || r.dateRaw || "") || "";
    const amt = toNumber(r.amount || r.amountNum || 0);
    if (sigSet.has(`${date}|${amt}`)) return true;
    return false;
  };
  return {
    filteredIncome: (income || []).filter(i => !isDup(i)),
    filteredExpense: (expense || []).filter(e => !isDup(e))
  };
}

export default function DailyJournal({ token, finYear }) {
  const [openingCash, setOpeningCash] = useState(0);
  const [openingBank, setOpeningBank] = useState(0);
  const [editingOpening, setEditingOpening] = useState(false);
  const [openingInput, setOpeningInput] = useState({ cash: "", bank: "" });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { start: fyStart, end: fyEnd } = finYearBounds(finYear);

  useEffect(() => {
    let mounted = true;
    async function fetchData() {
      setLoading(true);
      setError("");
      try {
        const obRes = await fetch(`${API_URL}/api/income_expense/summary/opening_balance?finyear=${finYear}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const obJson = obRes.ok ? await obRes.json().catch(() => ({})) : {};
        if (!mounted) return;
        setOpeningCash(toNumber(obJson.cash_opening || 0));
        setOpeningBank(toNumber(obJson.bank_opening || 0));
        setOpeningInput({
          cash: obJson.cash_opening !== undefined && obJson.cash_opening !== null ? String(obJson.cash_opening) : "",
          bank: obJson.bank_opening !== undefined && obJson.bank_opening !== null ? String(obJson.bank_opening) : ""
        });

        const incP = fetch(`${API_URL}/api/income_expense/income?finyear=${finYear}`, { headers: { Authorization: `Bearer ${token}` } }).catch(()=>({ ok:false }));
        const expP = fetch(`${API_URL}/api/income_expense/expense?finyear=${finYear}`, { headers: { Authorization: `Bearer ${token}` } }).catch(()=>({ ok:false }));
        const trfP = fetch(`${API_URL}/api/income_expense/transfers?finyear=${finYear}`, { headers: { Authorization: `Bearer ${token}` } }).catch(()=>({ ok:false }));

        const [incRes, expRes, trfRes] = await Promise.all([incP, expP, trfP]);

        const incRaw = incRes && incRes.ok ? await incRes.json().catch(()=>[]) : [];
        const expRaw = expRes && expRes.ok ? await expRes.json().catch(()=>[]) : [];
        const trfRaw = trfRes && trfRes.ok ? await trfRes.json().catch(()=>[]) : [];

        const normIncome = (incRaw || []).map(i => {
          const ymd = parseToYMD(i.date);
          const voucher = i.receipt_no || i.voucher_no || i.id || i.reference || "";
          const isRefund = (i.is_refund || (String(i.account_head || "").toLowerCase().includes("refund")) || (toNumber(i.amount || i.amount_paid || 0) < 0));
          return {
            original: i,
            rowType: isRefund ? "expense" : "income",
            ymd,
            dateRaw: i.date,
            amountNum: toNumber(i.amount ?? i.amount_paid ?? 0),
            mode: i.mode || "",
            voucher
          };
        }).filter(r => inRangeYmd(r.ymd, fyStart, fyEnd));

        const normExpense = (expRaw || []).map(e => {
          const ymd = parseToYMD(e.date);
          const voucher = e.voucher_no || e.receipt_no || e.id || e.reference || "";
          return {
            original: e,
            rowType: "expense",
            ymd,
            dateRaw: e.date,
            amountNum: toNumber(e.amount || 0),
            mode: e.mode || "",
            voucher
          };
        }).filter(r => inRangeYmd(r.ymd, fyStart, fyEnd));

        const normTransfers = (trfRaw || []).map(t => {
          const ymd = parseToYMD(t.date);
          const voucher = t.voucher_no || t.voucher || t.id || t.reference || "";
          return {
            original: t,
            rowType: "transfer",
            ymd,
            dateRaw: t.date,
            amountNum: toNumber(t.amount || 0),
            from_account: (t.from_account || "").toLowerCase(),
            to_account: (t.to_account || "").toLowerCase(),
            voucher
          };
        }).filter(r => inRangeYmd(r.ymd, fyStart, fyEnd));

        const { filteredIncome, filteredExpense } = filterOutTransferDuplicatesRaw(
          normIncome.map(i => ({ ...i, date: i.dateRaw })),
          normExpense.map(e => ({ ...e, date: e.dateRaw })),
          normTransfers.map(t => ({ ...t, date: t.dateRaw }))
        );

        const expenseByVoucher = new Map();
        const expenseBySig = new Map();
        (filteredExpense || []).forEach(e => {
          const keyV = normalizeId(e.voucher || "");
          const sig = `${e.ymd}|${toNumber(e.amountNum || e.amount || 0)}`;
          if (keyV) expenseByVoucher.set(keyV, e);
          if (!expenseBySig.has(sig)) expenseBySig.set(sig, e);
        });

        const usedIncomeKeys = new Set();
        const usedExpenseKeys = new Set();
        const inferredTransfers = [];

        (filteredIncome || []).forEach(inc => {
          const keyV = normalizeId(inc.voucher || "");
          const sig = `${inc.ymd}|${toNumber(inc.amountNum || inc.amount || 0)}`;
          let match = null;
          if (keyV && expenseByVoucher.has(keyV)) {
            match = expenseByVoucher.get(keyV);
          } else if (expenseBySig.has(sig)) {
            match = expenseBySig.get(sig);
          }
          if (!match) return;
          const incKey = inc.original?.id ?? inc.voucher ?? JSON.stringify(inc);
          const expKey = match.original?.id ?? match.voucher ?? JSON.stringify(match);
          if (usedIncomeKeys.has(incKey) || usedExpenseKeys.has(expKey)) return;
          usedIncomeKeys.add(incKey);
          usedExpenseKeys.add(expKey);
          const bankModes = ["bank", "online", "upi", "neft", "rtgs", "imps"];
          const incMode = (inc.mode || "").toLowerCase();
          const expMode = (match.mode || "").toLowerCase();
          let toAccount = "cash";
          let fromAccount = "cash";
          if (bankModes.includes(incMode)) toAccount = "bank";
          if (bankModes.includes(expMode)) fromAccount = "bank";
          inferredTransfers.push({
            type: "transfer",
            ymd: inc.ymd || match.ymd,
            dateRaw: inc.dateRaw || match.dateRaw,
            voucher: inc.voucher || match.voucher || "",
            particulars: inc.original?.particulars || match.original?.particulars || "",
            amountNum: Math.abs(toNumber(inc.amountNum || inc.amount || 0)),
            from_account: fromAccount,
            to_account: toAccount
          });
        });

        const remainingIncome = (filteredIncome || []).filter(i => {
          const key = i.original?.id ?? i.voucher ?? JSON.stringify(i);
          return !usedIncomeKeys.has(key);
        });
        const remainingExpense = (filteredExpense || []).filter(e => {
          const key = e.original?.id ?? e.voucher ?? JSON.stringify(e);
          return !usedExpenseKeys.has(key);
        });

        // -- Each row expresses only one type (income or expense), or a transfer.
        const incomeRows = (remainingIncome || []).map(i => ({
          type: i.rowType === "expense" ? "expense" : "income",
          ymd: i.ymd,
          date: formatDisplay(i.dateRaw),
          voucher: i.voucher || "",
          particulars: (i.original && (i.original.particulars || i.original.account_head || "")) || "",
          amount: Math.abs(toNumber(i.amountNum || i.amount || 0)),
          mode: i.mode || (i.original && i.original.mode) || ""
        }));
        const expenseRows = (remainingExpense || []).map(e => ({
          type: "expense",
          ymd: e.ymd,
          date: formatDisplay(e.dateRaw),
          voucher: e.voucher || "",
          particulars: (e.original && (e.original.particulars || e.original.account_head || "")) || "",
          amount: Math.abs(toNumber(e.amountNum || e.amount || 0)),
          mode: e.mode || (e.original && e.original.mode) || ""
        }));
        const transferRows = (normTransfers || []).map(t => ({
          type: "transfer",
          ymd: t.ymd,
          date: formatDisplay(t.dateRaw),
          voucher: t.voucher || "",
          particulars: (t.original && (t.original.particulars || t.original.reference || "")) || "",
          amount: Math.abs(toNumber(t.amountNum || t.amount || 0)),
          from_account: t.from_account,
          to_account: t.to_account
        }));
        const inferredRows = (inferredTransfers || []).map(m => ({
          type: "transfer",
          ymd: m.ymd,
          date: formatDisplay(m.dateRaw),
          voucher: m.voucher || "",
          particulars: m.particulars || "",
          amount: Math.abs(toNumber(m.amountNum || m.amount || 0)),
          from_account: m.from_account,
          to_account: m.to_account
        }));

        const unified = [...incomeRows, ...expenseRows, ...transferRows, ...inferredRows].sort((a, b) => {
          const ta = safeTs(a.ymd || a.date || "");
          const tb = safeTs(b.ymd || b.date || "");
          if (ta !== tb) return ta - tb;
          const order = { income: 0, transfer: 1, expense: 2 };
          return (order[a.type] || 1) - (order[b.type] || 1);
        });

        if (!mounted) return;
        setTransactions(unified);
      } catch (err) {
        console.error("DailyJournal fetch error", err);
        if (!mounted) return;
        setError("Failed to load journal data.");
        setTransactions([]);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    fetchData();
    return () => { mounted = false; };
  }, [token, finYear, fyStart, fyEnd]);

  const handleOpeningSave = async () => {
    setEditingOpening(false);
    const cashNum = toNumber(openingInput.cash);
    const bankNum = toNumber(openingInput.bank);
    setOpeningCash(cashNum);
    setOpeningBank(bankNum);
    try {
      await fetch(`${API_URL}/api/income_expense/summary/opening_balance`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ finyear: finYear, cash_opening: cashNum, bank_opening: bankNum })
      });
    } catch {
      setError("Failed to save opening balance.");
    }
  };

  function buildRowsWithBalances() {
    const rows = [];
    let runningCash = toNumber(openingCash);
    let runningBank = toNumber(openingBank);
    const asc = [...transactions];
    asc.forEach(t => {
      const amt = toNumber(t.amount || 0);
      const isIncome = t.type === "income";
      const isExpense = t.type === "expense";
      const isTransfer = t.type === "transfer";
      const bankModes = ["bank", "online", "upi", "neft", "rtgs", "imps"];
      const row = {
        receiptDate: "",
        receiptVoucher: "",
        receiptParticulars: "",
        receiptAmount: "",
        paymentDate: "",
        paymentVoucher: "",
        paymentParticulars: "",
        paymentAmount: "",
        runningCash: 0,
        runningBank: 0
      };
      if (isIncome) {
        const mode = (t.mode || "").toLowerCase();
        if (bankModes.includes(mode)) runningBank += amt;
        else runningCash += amt;
        row.receiptDate = t.date || "";
        row.receiptVoucher = t.voucher || "";
        row.receiptParticulars = t.particulars || "";
        row.receiptAmount = amt;
      }
      if (isExpense) {
        const mode = (t.mode || "").toLowerCase();
        if (bankModes.includes(mode)) runningBank -= amt;
        else runningCash -= amt;
        row.paymentDate = t.date || "";
        row.paymentVoucher = t.voucher || "";
        row.paymentParticulars = t.particulars || "";
        row.paymentAmount = amt;
      }
      if (isTransfer) {
        const from = (t.from_account || "").toLowerCase();
        const to = (t.to_account || "").toLowerCase();
        if (from === "cash") runningCash -= amt;
        else if (from === "bank") runningBank -= amt;
        if (to === "cash") runningCash += amt;
        else if (to === "bank") runningBank += amt;
        if (to) {
          row.receiptDate = t.date || "";
          row.receiptVoucher = t.voucher || "";
          row.receiptParticulars = t.particulars ? `${t.particulars} (Deposit)` : (to === "bank" ? "Bank Deposit" : "Cash In");
          row.receiptAmount = amt;
        }
        if (from) {
          row.paymentDate = t.date || "";
          row.paymentVoucher = t.voucher || "";
          row.paymentParticulars = t.particulars ? `${t.particulars} (Transfer Out)` : (from === "cash" ? "Cash → Bank" : "Bank → Cash");
          row.paymentAmount = amt;
        }
      }
      row.runningCash = runningCash;
      row.runningBank = runningBank;
      rows.push(row);
    });
    return rows;
  }

  const handleDownloadExcel = () => {
    const rows = buildRowsWithBalances();
    const sheet = rows.map(r => ({
      "Receipt Date": r.receiptDate || "",
      "Receipt Voucher No": r.receiptVoucher || "",
      "Receipt Particulars": r.receiptParticulars || "",
      "Receipt Amount (₹)": r.receiptAmount || "",
      "Payment Date": r.paymentDate || "",
      "Payment Voucher No": r.paymentVoucher || "",
      "Payment Particulars": r.paymentParticulars || "",
      "Payment Amount (₹)": r.paymentAmount || "",
      "Cash (₹)": r.runningCash,
      "Bank (₹)": r.runningBank
    }));
    const ws = XLSX.utils.json_to_sheet(sheet);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DailyJournal");
    XLSX.writeFile(wb, `DailyJournal_${finYear || "all"}_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const handleDownloadPDF = () => {
    const rows = buildRowsWithBalances();
    const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 36;
    const startY = 84;

    const head = [
      [
        { content: "Receipts", colSpan: 4, styles: { halign: "center", fillColor: [231,245,255], textColor: [34,68,136], fontStyle: "bold" } },
        { content: "Payments", colSpan: 4, styles: { halign: "center", fillColor: [255,235,235], textColor: [180,40,50], fontStyle: "bold" } },
        { content: "Balance (Cash | Bank)", colSpan: 1, styles: { halign: "center", fillColor: [245,245,245], textColor: [34,68,136], fontStyle: "bold" } }
      ],
      [
        "Receipt Date", "Receipt Voucher No", "Receipt Particulars", "Receipt Amount (₹)",
        "Payment Date", "Payment Voucher No", "Payment Particulars", "Payment Amount (₹)",
        "Cash (₹) | Bank (₹)"
      ]
    ];

    const body = rows.map(r => [
      r.receiptDate || "",
      r.receiptVoucher || "",
      r.receiptParticulars || "",
      r.receiptAmount ? to2(r.receiptAmount) : "",
      r.paymentDate || "",
      r.paymentVoucher || "",
      r.paymentParticulars || "",
      r.paymentAmount ? to2(r.paymentAmount) : "",
      `${to2(r.runningCash)} | ${to2(r.runningBank)}`
    ]);

    autoTable(doc, {
      startY,
      head,
      body,
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 4, overflow: "linebreak", valign: "middle" },
      headStyles: { fillColor: [244,247,255], textColor: [34,68,136], fontStyle: "bold" },
      didDrawPage: () => {
        doc.setFontSize(16);
        doc.setTextColor(34,68,136);
        doc.text(`Daily Journal - ${finYear || "All"}`, pageWidth / 2, 40, { align: "center" });
        doc.setFontSize(9);
        doc.setTextColor(80,80,80);
        doc.text(`Opening Cash: ${to2(openingCash)}    Opening Bank: ${to2(openingBank)}`, margin, 60);
        const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
        doc.setFontSize(8);
        doc.setTextColor(110);
        doc.text(`Page ${pageNumber}`, pageWidth - margin, pageHeight - 10, { align: "right" });
      }
    });

    const finalY = (doc.lastAutoTable && doc.lastAutoTable.finalY) ? doc.lastAutoTable.finalY + 12 : startY + 12;
    const totals = rows.reduce((acc, r) => {
      acc.income += Number(r.receiptAmount || 0);
      acc.expense += Number(r.paymentAmount || 0);
      return acc;
    }, { income: 0, expense: 0 });

    doc.setFontSize(10);
    doc.setTextColor(34,68,136);
    doc.setFont(undefined, "bold");
    doc.text(`Total Receipt: ${to2(totals.income)}`, margin + 6, finalY);
    doc.text(`Total Payment: ${to2(totals.expense)}`, pageWidth - margin - 160, finalY);

    const latest = rows.length ? rows[rows.length - 1] : null;
    doc.setFont(undefined, "normal");
    doc.text(`Closing Cash / Bank: ${to2(latest ? latest.runningCash : openingCash)} | ${to2(latest ? latest.runningBank : openingBank)}`, margin + 6, finalY + 18);

    doc.save(`DailyJournal_${finYear || "all"}_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  let totalIncome = 0;
  let totalExpense = 0;
  let closingCash = toNumber(openingCash);
  let closingBank = toNumber(openingBank);

  transactions.forEach(t => {
    const amt = toNumber(t.amount || 0);
    if (t.type === "income") {
      totalIncome += amt;
      const m = (t.mode || "").toLowerCase();
      if (["bank", "online", "upi", "neft", "rtgs"].includes(m)) closingBank += amt;
      else closingCash += amt;
    } else if (t.type === "expense") {
      totalExpense += amt;
      const m = (t.mode || "").toLowerCase();
      if (["bank", "online", "upi", "neft", "rtgs"].includes(m)) closingBank -= amt;
      else closingCash -= amt;
    } else if (t.type === "transfer") {
      const from = (t.from_account || "").toLowerCase();
      const to = (t.to_account || "").toLowerCase();
      if (from === "cash") closingCash -= amt;
      else if (from === "bank") closingBank -= amt;
      if (to === "cash") closingCash += amt;
      else if (to === "bank") closingBank += amt;
    }
  });

  return (
    <div className="daily-journal-container">
      <div className="dj-panel">
        <h2 className="dj-title">Daily Journal</h2>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <div>
            <button className="btn-save" onClick={handleDownloadExcel} style={{ marginRight: 8 }}>Download Excel</button>
            <button className="btn-save" onClick={handleDownloadPDF}>Download PDF</button>
          </div>
          <div style={{ fontSize: 14, color: "#205080" }}>
            {loading ? null : <>Opening: Cash {to2(openingCash)} | Bank {to2(openingBank)}</>}
          </div>
        </div>
        {error && <div className="dj-error">{error}</div>}
        {loading ? (
          <div className="dj-loading">Loading...</div>
        ) : (
          <table className="daily-journal-table">
            <colgroup>
              <col style={{ width: "110px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "320px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "110px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "320px" }} />
              <col style={{ width: "120px" }} />
              <col style={{ width: "160px" }} />
            </colgroup>
            <thead>
              <tr>
                <th colSpan={4} className="dj-th dj-income dj-heading-center">Receipts</th>
                <th colSpan={4} className="dj-th dj-payment dj-heading-center">Payments</th>
                <th className="dj-th dj-heading-center">Balance (₹)<br /><span style={{ fontWeight: "normal" }}>Cash | Bank</span></th>
              </tr>
              <tr>
                <th>Date</th>
                <th>Voucher No.</th>
                <th className="particulars-header">Particulars</th>
                <th className="amount-header">Amount (₹)</th>
                <th>Date</th>
                <th>Voucher No.</th>
                <th className="particulars-header">Particulars</th>
                <th className="amount-header">Amount (₹)</th>
                <th>Balance (₹)</th>
              </tr>
              <tr>
                <td colSpan={8} className="dj-opening dj-opening-label">
                  {editingOpening ? (
                    <>
                      Opening Cash Balance:
                      <input type="number" step="0.01" min={0} value={openingInput.cash} onChange={e => setOpeningInput(ob => ({ ...ob, cash: e.target.value }))} className="dj-opening-input" style={{ marginLeft: 5, marginRight: 20 }} />
                      Opening Bank Balance:
                      <input type="number" step="0.01" min={0} value={openingInput.bank} onChange={e => setOpeningInput(ob => ({ ...ob, bank: e.target.value }))} className="dj-opening-input" style={{ marginLeft: 5 }} />
                      <button className="btn-save" style={{ marginLeft: 10 }} onClick={handleOpeningSave}>Save</button>
                    </>
                  ) : (
                    <>
                      Opening Cash Balance: <span style={{ marginRight: 20 }}>{to2(openingCash)}</span>
                      Opening Bank Balance: <span>{to2(openingBank)}</span>
                      <button className="btn-edit" style={{ marginLeft: 10 }} onClick={() => setEditingOpening(true)}>Edit</button>
                    </>
                  )}
                </td>
                <td className="dj-opening dj-opening-balance">
                  Cash: {to2(openingCash)} | Bank: {to2(openingBank)}
                </td>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: "center", padding: 24 }}>No transactions found for selected financial year.</td>
                </tr>
              ) : (
                buildRowsWithBalances().map((r, idx) => (
                  <tr key={idx} className={idx % 2 === 1 ? "dj-row-alt" : ""}>
                    <td className="date">{r.receiptDate || ""}</td>
                    <td className="voucher-no">{r.receiptVoucher || ""}</td>
                    <td className="particulars">{r.receiptParticulars || ""}</td>
                    <td className="amount">{r.receiptAmount ? to2(r.receiptAmount) : ""}</td>
                    <td className="date">{r.paymentDate || ""}</td>
                    <td className="voucher-no">{r.paymentVoucher || ""}</td>
                    <td className="particulars">{r.paymentParticulars || ""}</td>
                    <td className="amount">{r.paymentAmount ? to2(r.paymentAmount) : ""}</td>
                    <td className="balance">{to2(r.runningCash)} | {to2(r.runningBank)}</td>
                  </tr>
                ))
              )}
              <tr>
                <td colSpan={4}></td>
                <td colSpan={4}></td>
                <td></td>
              </tr>
              <tr>
                <td colSpan={4} className="dj-total-label" style={{ textAlign: "left" }}>
                  Total Receipt: {to2(totalIncome)}
                </td>
                <td colSpan={4} className="dj-total-label" style={{ textAlign: "right" }}>
                  Total Payment: {to2(totalExpense)}
                </td>
                <td></td>
              </tr>
              <tr>
                <td colSpan={4}></td>
                <td colSpan={4} className="dj-closing-label" style={{ textAlign: "right" }}>
                  Closing Cash / Bank
                </td>
                <td className="dj-closing-value">{to2(closingCash)} | {to2(closingBank)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
